import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { trackBrowsingActivity } from '@/data/browsingActivity';

/**
 * Fetches the current user's favorite products.
 * @returns Query containing array of favorites with product_id (slug) and created_at
 */
export const useFavorites = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['favorites', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('user_favorites')
        .select('product_id, created_at')
        .eq('user_id', user.id);

      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
    staleTime: 60000, // Cache for 60s
  });
};

/**
 * Toggles a product as favorite for the current user.
 * @param productId - The product slug (NOT the UUID). Should match the slug field in products table.
 */
export const useToggleFavorite = (productId: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Must be logged in to favorite products');

      // Check if already favorited
      const { data: existing } = await supabase
        .from('user_favorites')
        .select('id')
        .eq('user_id', user.id)
        .eq('product_id', productId)
        .maybeSingle();

      if (existing) {
        // Remove favorite
        const { error } = await supabase
          .from('user_favorites')
          .delete()
          .eq('id', existing.id);

        if (error) throw error;
        return 'removed';
      } else {
        // Add favorite
        const { error } = await supabase
          .from('user_favorites')
          .insert({ user_id: user.id, product_id: productId });

        if (error) throw error;
        return 'added';
      }
    },
    onMutate: async () => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['favorites', user?.id] });
      
      // Snapshot previous value
      const previousFavorites = queryClient.getQueryData(['favorites', user?.id]);
      
      // Optimistically update to new value
      queryClient.setQueryData(['favorites', user?.id], (old: any) => {
        const oldFavorites = old || [];
        const exists = oldFavorites.some((fav: any) => fav.product_id === productId);
        if (exists) {
          return oldFavorites.filter((fav: any) => fav.product_id !== productId);
        } else {
          return [...oldFavorites, { product_id: productId, created_at: new Date().toISOString() }];
        }
      });
      
      return { previousFavorites };
    },
    onSuccess: async (action) => {
      queryClient.invalidateQueries({ queryKey: ['favorites', user?.id] });
      toast({
        title: action === 'added' ? 'Added to favorites' : 'Removed from favorites',
        description: action === 'added' ? 'Product saved to your favorites' : 'Product removed from favorites',
      });

      // Track favorite/unfavorite activity
      if (user?.id) {
        // Get product UUID from slug
        const { data: product } = await supabase
          .from('products')
          .select('id')
          .eq('slug', productId)
          .maybeSingle();

        if (product?.id) {
          try {
            await trackBrowsingActivity({
              userId: user.id,
              productId: product.id,
              activityType: action === 'added' ? 'favorite' : 'unfavorite',
            });
          } catch (error) {
            // Silent fail - don't disrupt UX for analytics
            console.warn('Failed to track favorite activity:', error);
          }
        }
      }
    },
    onError: (error: Error, variables, context: any) => {
      // Rollback on error
      if (context?.previousFavorites) {
        queryClient.setQueryData(['favorites', user?.id], context.previousFavorites);
      }
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Phase 3: useIsFavorited removed - now computed at data layer for performance
